// MSSQL procedural extras — tree-sitter-tokenized scanners that surface
// procedural-body context to the completion candidate builder. Two scanners:
//
//   1. `findEnclosingTriggerHostTable(source, offset)` — locates the
//      `CREATE [OR ALTER] TRIGGER … ON <table>` that wraps the cursor and
//      returns the host table. Lets `inserted.|` / `deleted.|` inside a
//      trigger body resolve to the trigger's host table when no DML
//      `OUTPUT` clause is in scope (the regular `findOutputDmlTarget` only
//      finds MERGE / INSERT / UPDATE / DELETE … OUTPUT contexts).
//
//   2. `isInsideCatchBlock(source, offset)` — boolean-only test for
//      whether the cursor lies inside an unmatched `BEGIN CATCH` block.
//      When true, the candidate builder emits the seven CATCH-context
//      error-state functions (`ERROR_MESSAGE`, `ERROR_NUMBER`,
//      `ERROR_LINE`, `ERROR_PROCEDURE`, `ERROR_SEVERITY`, `ERROR_STATE`,
//      and the closely-related `XACT_STATE`).
//
// Pure module — no Monaco / electron imports. Operates on the raw source
// + offset pair already produced upstream. Mirrors the style of
// `mssqlExtras.ts` (whole-buffer scan, `tokenize()` shared with the
// position-kind state machine).

import { tokenize, type Token } from '../context/tokenizer';
import { parseIdentifierToken } from '../resolver/identifierUtils';

export type MssqlTriggerHostTable = {
  readonly schema?: string;
  readonly name: string;
};

const ENGINE = 'mssql' as const;

export function findEnclosingTriggerHostTable(
  source: string,
  offset: number,
): MssqlTriggerHostTable | null {
  // Scan tokens from buffer start up to the cursor, tracking the most
  // recent `CREATE [OR ALTER] TRIGGER … ON <table>` whose body still
  // wraps the cursor. We can't cheaply detect "trigger body ends" from
  // tokens alone (TRIGGER bodies end at the matching `END` of an `AS BEGIN`
  // pair, optionally followed by `GO`); the reliable signal is the next
  // `GO` batch separator OR the next top-level `CREATE` of any kind.
  // Conservative: any `CREATE` (other than `CREATE OR ALTER` of the same
  // TRIGGER) terminates the previous trigger's reach for our purposes.
  const tokens = tokenize(source, 0, source.length, ENGINE);
  let active: { host: MssqlTriggerHostTable; bodyStart: number } | null = null;
  for (let i = 0; i < tokens.length; i += 1) {
    const tok = tokens[i];
    if (tok.start >= offset) break;
    const upper = identUpper(tok);
    if (!upper) continue;
    if (upper === 'GO') {
      active = null;
      continue;
    }
    if (upper === 'CREATE') {
      const after = skipCreateOrAlterPreamble(tokens, i + 1);
      const kindTok = tokens[after];
      const kindUpper = identUpper(kindTok);
      if (kindUpper === 'TRIGGER') {
        const onIdx = findNextOnIdx(tokens, after + 1);
        if (onIdx === -1) {
          active = null;
          continue;
        }
        const target = readQualifiedName(tokens, onIdx + 1);
        if (!target) {
          active = null;
          continue;
        }
        active = { host: target.target, bodyStart: tokens[after].end };
        i = target.lastIdx;
        continue;
      }
      // Any non-TRIGGER CREATE terminates the previous trigger's reach.
      active = null;
      continue;
    }
  }
  return active ? active.host : null;
}

export function isInsideCatchBlock(source: string, offset: number): boolean {
  // Walk tokens up to the cursor, maintaining the delta of
  // unmatched BEGIN CATCH / END CATCH pairs. When the delta is positive at
  // the cursor offset, the cursor is inside a CATCH block.
  const tokens = tokenize(source, 0, source.length, ENGINE);
  let depth = 0;
  for (let i = 0; i < tokens.length; i += 1) {
    const tok = tokens[i];
    if (tok.start >= offset) break;
    const upper = identUpper(tok);
    if (!upper) continue;
    if (upper === 'BEGIN' && identUpper(tokens[i + 1]) === 'CATCH') {
      depth += 1;
      i += 1;
      continue;
    }
    if (upper === 'END' && identUpper(tokens[i + 1]) === 'CATCH') {
      if (depth > 0) depth -= 1;
      i += 1;
      continue;
    }
  }
  return depth > 0;
}

export type MssqlCatchErrorFunction = {
  readonly name: string;
  readonly snippetInsertText: string;
  readonly detail: string;
};

export const MSSQL_CATCH_ERROR_FUNCTIONS: readonly MssqlCatchErrorFunction[] = [
  { name: 'ERROR_MESSAGE', snippetInsertText: 'ERROR_MESSAGE()', detail: 'CATCH error-state function — message text' },
  { name: 'ERROR_NUMBER', snippetInsertText: 'ERROR_NUMBER()', detail: 'CATCH error-state function — error number' },
  { name: 'ERROR_LINE', snippetInsertText: 'ERROR_LINE()', detail: 'CATCH error-state function — line number' },
  { name: 'ERROR_PROCEDURE', snippetInsertText: 'ERROR_PROCEDURE()', detail: 'CATCH error-state function — procedure name' },
  { name: 'ERROR_SEVERITY', snippetInsertText: 'ERROR_SEVERITY()', detail: 'CATCH error-state function — severity' },
  { name: 'ERROR_STATE', snippetInsertText: 'ERROR_STATE()', detail: 'CATCH error-state function — state' },
  { name: 'XACT_STATE', snippetInsertText: 'XACT_STATE()', detail: 'Transaction state probe (CATCH-friendly)' },
];

function skipCreateOrAlterPreamble(tokens: readonly Token[], idx: number): number {
  // After `CREATE` we may see `OR ALTER` (T-SQL 2016 SP1+) before the
  // entity kind. Walk past it.
  if (identUpper(tokens[idx]) === 'OR' && identUpper(tokens[idx + 1]) === 'ALTER') {
    return idx + 2;
  }
  return idx;
}

function findNextOnIdx(tokens: readonly Token[], from: number): number {
  // Bounded forward scan. The trigger header is short (≤ 32 tokens to ON);
  // the bound limits worst-case work on malformed buffers without
  // capping correctness on real inputs.
  const limit = Math.min(tokens.length, from + 32);
  for (let i = from; i < limit; i += 1) {
    if (identUpper(tokens[i]) === 'ON') return i;
  }
  return -1;
}

function readQualifiedName(
  tokens: readonly Token[],
  startIdx: number,
): { target: MssqlTriggerHostTable; lastIdx: number } | null {
  if (startIdx >= tokens.length) return null;
  const head = tokens[startIdx];
  if (!head || head.type !== 'ident') return null;
  const parts: string[] = [parseIdentifierToken(head.text).content];
  let i = startIdx + 1;
  while (i < tokens.length && tokens[i].type === 'dot') {
    const next = tokens[i + 1];
    if (!next || (next.type !== 'ident' && next.type !== 'kw')) break;
    parts.push(parseIdentifierToken(next.text).content);
    i += 2;
  }
  const name = parts[parts.length - 1]!;
  const schema = parts.length >= 2 ? parts[parts.length - 2] : undefined;
  return { target: schema ? { schema, name } : { name }, lastIdx: i - 1 };
}

function identUpper(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return t.text.toUpperCase();
}
